import { getDaysInCurrentMonth, getToday } from "./calendarUtils";
import { toLocalDateString } from "./dateUtils";

/**
 * Coerces a stored amount to a number. Amounts arrive as JSON numbers from the
 * API but as strings from an in-progress edit, so both shapes reach these
 * helpers.
 * @returns {number} 0 for anything unparseable, so one bad row can't NaN out a total.
 */
const toAmount = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

/**
 * Total spent on the current local calendar date.
 *
 * Compares YYYY-MM-DD strings directly: expense dates are stored as the local
 * date they were entered on, and toLocalDateString() produces today's local
 * date in the same shape.
 */
export const calculateTotalToday = (expenses = []) => {
  const today = toLocalDateString();
  return expenses
    .filter((expense) => expense.date === today)
    .reduce((total, expense) => total + toAmount(expense.amount), 0);
};

/**
 * Sums every expense passed in. Callers are responsible for supplying a list
 * that is already scoped to the month they care about — getExpenses() queries
 * by month, so its results already are.
 */
export const calculateTotal = (expenses = []) =>
  expenses.reduce((total, expense) => total + toAmount(expense.amount), 0);

/**
 * How much under (positive) or over (negative) the expected pace of spending
 * the user is, in currency units.
 */
export const calculateBudgetDiff = (expenses, budget) => {
  const monthlyBudget = toAmount(budget);
  const expectedSoFar = (monthlyBudget / getDaysInCurrentMonth()) * getToday();
  return expectedSoFar - calculateTotal(expenses);
};

/**
 * Budget pace as a plain value, for the caller to render however it likes.
 * @returns {{ amount: number, isUnder: boolean }} amount is always non-negative.
 */
export const getBudgetPace = (expenses, budget) => {
  const diff = calculateBudgetDiff(expenses, budget);
  return { amount: Math.abs(diff), isUnder: diff >= 0 };
};

/**
 * Percentage of the monthly budget spent (0-100+).
 * Returns 0 for a missing or non-positive budget rather than Infinity/NaN,
 * which would render as a `width: NaN%` style.
 * @returns {number}
 */
export const calculatePercentThruBudget = (expenses, budget) => {
  const monthlyBudget = toAmount(budget);
  if (monthlyBudget <= 0) return 0;
  return (calculateTotal(expenses) / monthlyBudget) * 100;
};

/**
 * Percentage of a target reached (0-100+). Identical math to
 * calculatePercentThruBudget, named for the income direction: a budget is a
 * ceiling you spend toward, a goal is a floor you earn toward, and reading
 * "percent thru budget" at an income call site inverts the meaning.
 * @returns {number}
 */
export const calculatePercentOfTarget = (entries, target) =>
  calculatePercentThruBudget(entries, target);

/**
 * Sums expenses per category.
 * @returns {Record<string, number>} Returned rather than pushed into a setter,
 * so callers can derive it and it can't drift out of sync with the list.
 */
export const calculateTotalsByCategory = (expenses = []) =>
  expenses.reduce((totals, expense) => {
    totals[expense.category] =
      (totals[expense.category] || 0) + toAmount(expense.amount);
    return totals;
  }, {});
